import React, { useState } from 'react';
import { Circle, Popup, Tooltip, useMap, useMapEvents } from 'react-leaflet';

import { libelle, nomPays, texteSur } from '../../lib/libelles';
import { ZOOM_ETIQUETTES_ZONES, familleFiltre } from './famillesZones';

const EMPTY_ARRAY = [];

/**
 * A zone reads on three axes: what it is (risk or not), and — when a route is
 * known — whether the convoy has already crossed it. Crossed zones are drawn
 * solid and upcoming ones dashed, so the distinction does not rest on hue
 * alone; the same rule the marker palette follows.
 */
const ZONE_STYLES = {
  risk: { color: 'hsl(var(--destructive))', fillOpacity: 0.18, dashArray: null },
  passed: { color: 'hsl(var(--cat-done))', fillOpacity: 0.14, dashArray: null },
  upcoming: { color: 'hsl(var(--cat-route))', fillOpacity: 0.1, dashArray: '8 6' },
  neutral: { color: 'hsl(var(--info))', fillOpacity: 0.18, dashArray: null },
};

export const zoneVariant = (zone) => {
  if (zone.type === 'risk_zone') return 'risk';
  if (zone.corridor === 'passed') return 'passed';
  if (zone.corridor === 'upcoming') return 'upcoming';
  return 'neutral';
};

/**
 * Deux lectures d'une même carte, et il fallait choisir laquelle par écran.
 *
 * Sur le détail d'un trajet, ce qui compte est l'avancement: zone franchie ou
 * zone à venir. Sur le référentiel des zones, l'avancement n'existe pas — ce
 * qui compte est la nature du lieu: on y stocke, on y livre, on le franchit.
 * Les deux axes sont indépendants, et les peindre avec la même palette faisait
 * mentir l'un des deux. D'où `colorBy`, qui laisse l'écran dire ce qu'il
 * raconte; `corridor` reste le défaut, donc les quatre appelants existants ne
 * changent pas de rendu.
 *
 * Les familles reprennent celles du référentiel (`GeoLocationsPage`), et pour
 * la même raison qu'elles y ont été regroupées: douze types ne font pas douze
 * couleurs. L'échelle catégorielle du produit ne compte que trois pas, mesurés
 * et validés; au-delà, deux teintes voisines deviennent indiscernables.
 */
const FAMILY_STYLES = {
  depot: { color: 'hsl(var(--cat-hold))', fillOpacity: 0.16 },
  delivery: { color: 'hsl(var(--cat-done))', fillOpacity: 0.14 },
  border: { color: 'hsl(var(--cat-route))', fillOpacity: 0.14 },
  risk: { color: 'hsl(var(--destructive))', fillOpacity: 0.18 },
  other: { color: 'hsl(var(--muted-foreground))', fillOpacity: 0.12 },
};

const TYPE_FAMILIES = {
  fuel_depot: 'depot',
  station: 'depot',
  port: 'depot',
  delivery_site: 'delivery',
  border_post: 'border',
  customs_post: 'border',
  checkpoint: 'border',
  risk_zone: 'risk',
};

export const zoneFamily = (zone) => TYPE_FAMILIES[zone?.type] || 'other';

export const ZONE_FAMILY_LABELS = {
  depot: 'Dépôt',
  delivery: 'Site de livraison',
  border: 'Poste frontière',
  risk: 'Zone à risque',
  other: 'Autre',
};

const CORRIDOR_LABELS = {
  passed: 'Zone franchie',
  upcoming: 'Zone à venir',
};

/**
 * Vrai quand la carte est assez zoomée pour que les noms de zones se lisent.
 * Seul le franchissement du seuil provoque un nouveau rendu, pas chaque zoom.
 */
function useZoomAtteint(seuil) {
  const carte = useMap();
  const [atteint, setAtteint] = useState(carte.getZoom() >= seuil);
  useMapEvents({ zoomend: () => setAtteint(carte.getZoom() >= seuil) });
  return atteint;
}

export default function ZoneLayer({
  zones = EMPTY_ARRAY,
  onSelect,
  interactive = true,
  /** `corridor` (défaut) ou `type` — voir `FAMILY_STYLES`. */
  colorBy = 'corridor',
  /** Étiquette permanente portant le nom de la zone. */
  showLabels = false,
  /** Zoom minimal pour afficher les étiquettes (elles s'empilent en vue large). */
  labelsMinZoom = ZOOM_ETIQUETTES_ZONES,
  /** Familles décochées dans la fenêtre « Zones » (voir `zonesFiltre.js`). */
  famillesMasquees = EMPTY_ARRAY,
}) {
  const etiquettesLisibles = useZoomAtteint(labelsMinZoom);
  return zones
    .filter(
      (zone) =>
        zone &&
        !famillesMasquees.includes(familleFiltre(zone)) &&
        zone.latitude != null &&
        zone.longitude != null &&
        zone.status !== 'inactive',
    )
    .map((zone) => {
      // Le trait pointillé reste attaché à l'avancement, quelle que soit la
      // palette: c'est le second encodage qui permet de lire la carte sans
      // dépendre de la teinte — la règle que suit déjà la palette des
      // marqueurs.
      const style =
        colorBy === 'type'
          ? {
              ...FAMILY_STYLES[zoneFamily(zone)],
              dashArray: zone.corridor === 'upcoming' ? '8 6' : null,
            }
          : ZONE_STYLES[zoneVariant(zone)];
      return (
        <Circle
          key={zone.id ?? `${zone.latitude},${zone.longitude}`}
          center={[zone.latitude, zone.longitude]}
          radius={Number(zone.radius_meters || 0)}
          interactive={interactive}
          pathOptions={{
            color: style.color,
            fillColor: style.color,
            fillOpacity: style.fillOpacity,
            weight: 2,
            dashArray: style.dashArray || undefined,
          }}
          eventHandlers={onSelect ? { click: () => onSelect(zone) } : undefined}
        >
          {showLabels && etiquettesLisibles && zone.name ? (
            <Tooltip
              permanent
              direction="top"
              offset={[0, -4]}
              className="!rounded-lg !border-border !bg-card !px-2 !py-0.5 !text-xs !font-semibold !text-foreground !shadow-sm"
            >
              {zone.name}
            </Tooltip>
          ) : null}
          {interactive && (
            <Popup>
              <div className="min-w-[200px] text-sm">
                <div className="font-semibold">{zone.name}</div>
                <div className="text-muted-foreground">
                  {libelle('locationType', zone.type, 'Lieu')}
                  {zone.country ? ` · ${nomPays(zone.country)}` : ''}
                  {zone.city ? ` · ${texteSur(zone.city, '')}` : ''}
                </div>
                <div>
                  Rayon : {Number(zone.radius_meters || 0).toLocaleString('fr-FR')} m
                </div>
                {CORRIDOR_LABELS[zone.corridor] && (
                  <div className="mt-1 font-medium">
                    {CORRIDOR_LABELS[zone.corridor]}
                  </div>
                )}
                {zone.description && (
                  <div className="mt-1 text-muted-foreground">{zone.description}</div>
                )}
              </div>
            </Popup>
          )}
        </Circle>
      );
    });
}
